import { toast } from "sonner";
import { valueToEditBuffer, editBufferToValue } from "./edit-buffer-conversion";
import type { SimpleFieldConfig, FieldValue, BaseEntity } from "../types";
import type { AuthenticatedUser } from "@/lib/types/unified";
import { isCrmUserFacingError } from "@/lib/utils/crm/mutation-error";

// ============================================================================
// INLINE EDITING LOGIC
// ============================================================================

/**
 * Handles starting cell editing with proper value formatting
 */
export const handleCellEdit = (
  rowId: string,
  fieldId: string,
  currentValue: FieldValue,
  fields: Record<string, SimpleFieldConfig>,
  setEditingCell: (cell: { rowId: string; fieldId: string } | null) => void,
  setEditValue: (value: string) => void
): void => {
  setEditingCell({ rowId, fieldId });

  // Format the initial value based on field type using the typed conversion module
  const fieldConfig = fields[fieldId];
  const initialValue = valueToEditBuffer(currentValue, fieldConfig);

  setEditValue(initialValue);
};

/**
 * Handles saving cell edits with proper data conversion
 */
export const handleCellSave = async (
  rowId: string,
  fieldId: string,
  editValue: string,
  data: BaseEntity[],
  fields: Record<string, SimpleFieldConfig>,
  onInlineEdit: (payload: Record<string, FieldValue>) => Promise<void>,
  _appUser?: AuthenticatedUser,
  setEditingCell?: (cell: { rowId: string; fieldId: string } | null) => void,
  setEditValue?: (value: string) => void,
  setSavingCell?: (cell: { rowId: string; fieldId: string } | null) => void,
  displayName: string = "Record"
): Promise<void> => {
  void _appUser;
  if (!onInlineEdit) return;

  try {
    // Find the row data
    const rowData = data.find((item) => item.id === rowId);
    if (!rowData) {
      toast.error(`${displayName} not found`);
      return;
    }

    // Convert the edit value to the appropriate data type using the typed conversion module
    const fieldConfig = fields[fieldId];
    if (!fieldConfig) {
      console.error(`Field config not found for field: ${fieldId}`);
      toast.error(`Field configuration error for ${fieldId}`);
      return;
    }

    const convertedValue: FieldValue = editBufferToValue(editValue, fieldConfig);

    // Create update payload with only the changed field
    const updatePayload = {
      ...rowData,
      [fieldId]: convertedValue,
    };

    // DEBUG: Log the date value through the flow
    if (fieldId === "due_date") {
      console.log("[DEBUG] handleCellSave - Due Date Flow:", {
        editValue,
        fieldConfig: fieldConfig.type,
        convertedValue,
        updatePayload: updatePayload[fieldId],
        fieldConfigExists: !!fieldConfig,
      });
    }

    try {
      await onInlineEdit(updatePayload);

      // Clear editing state only on success
      setEditingCell?.(null);
      setEditValue?.("");
      setSavingCell?.(null);

      // Don't show success toast here since onInlineEdit handles it
    } catch (inlineEditError) {
      // Re-throw the error so it can be handled by the caller
      // Don't clear editing state on error - let user retry or cancel manually
      console.error("Inline edit error:", inlineEditError);
      throw inlineEditError;
    }
  } catch (error) {
    // Only show error toast if it's not already handled
    const errorMessage = error instanceof Error ? error.message : "Unknown error";
    console.error("Edit error:", error);

    // Don't clear editing state on error - let user see what they typed and retry or cancel
    // This prevents data loss
    //
    // A CrmUserFacingError already IS the explanation (e.g. "That phone number is
    // already on “Jane Doe”. … put it on the COMPANY instead …"), so prefixing it
    // reads as two stacked errors and buries the part that says what to do. Show
    // it alone; keep the prefix for technical failures, where it adds the context
    // the raw message lacks.
    toast.error(
      isCrmUserFacingError(error) ? errorMessage : `Failed to update field: ${errorMessage}`
    );

    // Re-throw so caller knows it failed
    throw error;
  }
};

/**
 * Handles canceling cell editing
 */
export const handleCellCancel = (
  setEditingCell: (cell: { rowId: string; fieldId: string } | null) => void,
  setEditValue: (value: string) => void,
  setSavingCell: (cell: { rowId: string; fieldId: string } | null) => void
): void => {
  setEditingCell(null);
  setEditValue("");
  setSavingCell(null);
};
